import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useWeb } from '../context/WebContext';
import { FaPhoneAlt, FaWhatsapp, FaEnvelope, FaMapMarkerAlt, FaClock, FaChevronDown, FaBars, FaTimes } from 'react-icons/fa';

export default function Layout({ children }) {
  const { settings, services } = useWeb();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(null);
  const location = useLocation();

  const categories = ['Bird Nets', 'Invisible Grills', 'Sports Nets', 'Cloth Hangers'];

  const handleWhatsappClick = () => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'whatsapp_click', {
        'event_category': 'Engagement',
        'event_label': 'WhatsApp Click'
      });
    }
    const cleanNum = settings.whatsappNumber.replace(/[^0-9]/g, '');
    window.open(`https://api.whatsapp.com/send?phone=${cleanNum}&text=Hi%20Vengamamba%20Enterprises,%20I%20am%20interested%20in%20your%20installation%20services.`, '_blank');
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Contact Us', path: '/contact' },
  ];

  const activeClass = (path) => 
    location.pathname === path 
      ? 'text-orange-600 font-semibold' 
      : 'text-slate-700 hover:text-orange-600 transition-colors duration-200';

  const activeMobileClass = (path) =>
    location.pathname === path
      ? 'bg-orange-50 text-orange-600 font-semibold px-4 py-2 rounded-lg'
      : 'text-slate-700 hover:bg-slate-50 px-4 py-2 rounded-lg transition-colors';

  return (
    <div className="min-h-screen flex flex-col font-sans bg-slate-50">
      {/* Top Utility Bar */}
      <div className="bg-slate-900 text-slate-300 text-xs py-2 px-4 border-b border-slate-800 hidden md:block">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-6">
            <span className="flex items-center space-x-2">
              <FaClock className="text-orange-500" />
              <span>{settings.workingHours}</span>
            </span>
            <span className="flex items-center space-x-2">
              <FaEnvelope className="text-orange-500" />
              <a href={`mailto:${settings.email}`} className="hover:text-white transition-colors">{settings.email}</a>
            </span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="flex items-center space-x-2">
              <FaMapMarkerAlt className="text-orange-500" />
              <span className="truncate max-w-xs">{settings.address.split(',')[2] || settings.address}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Main Header / Navbar */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="/logo.png" 
              alt={settings.companyName} 
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.slice(0, 2).map((link) => (
              <Link key={link.name} to={link.path} className={activeClass(link.path)}>
                {link.name}
              </Link>
            ))}

            {/* Services Mega Menu Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setServicesDropdownOpen(true)}
              onMouseLeave={() => setServicesDropdownOpen(false)}
            >
              <Link 
                to="/services" 
                className="flex items-center space-x-1 py-2 text-slate-700 hover:text-orange-600 transition-colors focus:outline-none"
              >
                <span>Services</span>
                <FaChevronDown className="text-xs transition-transform duration-200" style={{ transform: servicesDropdownOpen ? 'rotate(180deg)' : 'none' }} />
              </Link>
              
              {servicesDropdownOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 mt-0 w-[940px] bg-white rounded-3xl shadow-2xl border border-slate-100 p-6 grid grid-cols-4 gap-6 z-50 text-left">
                  {categories.map((cat) => (
                    <div key={cat} className="space-y-3">
                      <h4 className="font-black text-xs uppercase tracking-wider text-orange-600 border-b border-slate-100 pb-2">{cat}</h4>
                      <div className="flex flex-col space-y-1.5 max-h-[480px] overflow-y-auto pr-1 scrollbar-thin">
                        {services.filter(s => s.category === cat).map((service) => (
                          <Link 
                            key={service.id} 
                            to={`/services/${service.id}`}
                            className="text-xs text-slate-700 hover:text-orange-600 hover:pl-1 transition-all duration-150 block py-0.5 font-medium"
                            onClick={() => setServicesDropdownOpen(false)}
                          >
                            {service.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {navLinks.slice(2).map((link) => (
              <Link key={link.name} to={link.path} className={activeClass(link.path)}>
                {link.name}
              </Link>
            ))}

          </nav>

          {/* Contact Buttons */}
          <div className="hidden sm:flex items-center space-x-4">
            <a 
              href={`tel:${settings.phone1}`} 
              onClick={() => gtag_report_conversion()}
              className="flex items-center justify-center space-x-2 bg-slate-900 text-white font-medium px-5 py-2.5 rounded-full hover:bg-slate-800 transition-all shadow-sm"
            >
              <FaPhoneAlt className="text-xs text-orange-500" />
              <span>Call Now</span>
            </a>
            <button 
              onClick={handleWhatsappClick}
              className="flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white font-medium px-5 py-2.5 rounded-full transition-all shadow-sm"
            >
              <FaWhatsapp className="text-lg" />
              <span>WhatsApp</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-slate-700 hover:text-orange-600 focus:outline-none p-2"
          >
            {mobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Sidebar Navigation */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden flex">
          <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}></div>
          <div className="relative ml-auto w-full max-w-xs bg-white h-full shadow-2xl flex flex-col py-6 overflow-y-auto px-4">
            <div className="flex justify-between items-center mb-8 border-b pb-4">
              <span className="font-bold text-slate-900">Vengamamba Safety</span>
              <button 
                onClick={() => setMobileMenuOpen(false)}
                className="text-slate-500 hover:text-orange-600 focus:outline-none"
              >
                <FaTimes size={20} />
              </button>
            </div>
            
            <nav className="flex flex-col space-y-4">
              <Link to="/" className={activeMobileClass('/')} onClick={() => setMobileMenuOpen(false)}>Home</Link>
              <Link to="/about" className={activeMobileClass('/about')} onClick={() => setMobileMenuOpen(false)}>About Us</Link>
              
              <div className="border-t border-b border-slate-100 py-2">
                <Link 
                  to="/services" 
                  className="px-4 py-1.5 text-sm font-bold text-slate-900 hover:text-orange-600 flex justify-between items-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span>Our Services</span>
                  <span className="text-xs text-orange-600 font-medium">View All</span>
                </Link>
                
                <div className="mt-2 pl-2 space-y-1">
                  {categories.map((cat) => (
                    <div key={cat} className="space-y-1">
                      <button
                        onClick={() => setMobileCategoryOpen(mobileCategoryOpen === cat ? null : cat)}
                        className="w-full text-left px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 flex justify-between items-center rounded-lg"
                      >
                        <span>{cat}</span>
                        <FaChevronDown 
                          className="text-[9px] transition-transform duration-200" 
                          style={{ transform: mobileCategoryOpen === cat ? 'rotate(180deg)' : 'none' }} 
                        />
                      </button>
                      
                      {mobileCategoryOpen === cat && (
                        <div className="pl-6 pr-2 py-1 flex flex-col space-y-1.5 border-l border-slate-150 ml-4 max-h-60 overflow-y-auto">
                          {services.filter(s => s.category === cat).map((service) => (
                            <Link 
                              key={service.id} 
                              to={`/services/${service.id}`}
                              className="text-xs text-slate-600 hover:text-orange-600 py-0.5 truncate block"
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {service.title}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <Link to="/gallery" className={activeMobileClass('/gallery')} onClick={() => setMobileMenuOpen(false)}>Gallery</Link>
              <Link to="/contact" className={activeMobileClass('/contact')} onClick={() => setMobileMenuOpen(false)}>Contact Us</Link>
            </nav>

            <div className="mt-auto space-y-3 pt-6 border-t">
              <a 
                href={`tel:${settings.phone1}`} 
                onClick={() => gtag_report_conversion()}
                className="flex items-center justify-center space-x-2 w-full bg-slate-900 text-white font-medium py-3 rounded-xl hover:bg-slate-800 transition"
              >
                <FaPhoneAlt className="text-orange-500" />
                <span>Call Now</span>
              </a>
              <button 
                onClick={handleWhatsappClick}
                className="flex items-center justify-center space-x-2 w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 rounded-xl transition"
              >
                <FaWhatsapp />
                <span>WhatsApp Enquiry</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Sticky Action Footer Bar for Mobile */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-100 grid grid-cols-2 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">
        <a 
          href={`tel:${settings.phone1}`} 
          onClick={() => gtag_report_conversion()}
          className="flex items-center justify-center space-x-2 py-3.5 bg-slate-900 text-white font-semibold text-center border-r border-slate-800 active:bg-slate-950"
        >
          <FaPhoneAlt className="text-orange-500" />
          <span>Call Now</span>
        </a>
        <button 
          onClick={handleWhatsappClick}
          className="flex items-center justify-center space-x-2 py-3.5 bg-green-600 hover:bg-green-700 text-white font-semibold text-center active:bg-green-800"
        >
          <FaWhatsapp className="text-lg" />
          <span>WhatsApp</span>
        </button>
      </div>

      {/* Floating Action Button for Desktop */}
      <div className="hidden sm:flex flex-col space-y-3 fixed bottom-8 right-8 z-40">
        <a 
          href={`tel:${settings.phone1}`} 
          onClick={() => gtag_report_conversion()}
          className="flex items-center justify-center bg-slate-950 text-white p-4 rounded-full shadow-2xl hover:scale-110 active:scale-95 transition hover:bg-slate-900 group"
          title="Call Now"
        >
          <FaPhoneAlt className="text-orange-500 group-hover:animate-bounce" />
        </a>
        <button 
          onClick={handleWhatsappClick}
          className="flex items-center justify-center bg-green-600 text-white p-4 rounded-full shadow-2xl hover:scale-110 active:scale-95 transition hover:bg-green-700 group"
          title="WhatsApp Us"
        >
          <FaWhatsapp className="text-xl group-hover:scale-125 transition-transform" />
        </button>
      </div>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 pt-8 pb-16 sm:pt-16 sm:pb-8 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-12 mb-8 sm:mb-12">
          {/* Company Brief */}
          <div className="space-y-3 col-span-2 lg:col-span-1">
            <div className="flex items-center">
              <span className="text-lg sm:text-xl font-bold tracking-tight text-white">
                VENGAMAMBA <span className="text-orange-500">ENTERPRISES</span>
              </span>
            </div>
            <p className="text-sm leading-relaxed hidden sm:block">
              Premium safety net installation service specializing in high-rise balconies, pigeon control, building safety, and invisible grills across India. Certified quality standards and professional staff.
            </p>
            <div className="text-xs sm:text-sm font-semibold text-white pt-1">
              Corporate Office: <br/>
              <span className="font-normal text-slate-400 text-xs">{settings.address}</span>
            </div>
          </div>

          <div className="col-span-1">
            <h4 className="text-white font-semibold text-sm sm:text-base mb-3 sm:mb-4 relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-8 after:h-0.5 after:bg-orange-500 pb-1.5">Quick Links</h4>
            <ul className="space-y-2 text-xs sm:text-sm">
              {navLinks.map((link) => (
                <li key={link.name}>
                  <Link to={link.path} className="hover:text-white transition-colors duration-200 block">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services List */}
          <div className="col-span-1">
            <h4 className="text-white font-semibold text-sm sm:text-base mb-3 sm:mb-4 relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-8 after:h-0.5 after:bg-orange-500 pb-1.5">Our Services</h4>
            <ul className="space-y-2 text-[10px] sm:text-xs grid grid-cols-1 gap-1">
              {services.slice(0, 7).map((service) => (
                <li key={service.id}>
                  <Link to={`/services/${service.id}`} className="hover:text-white transition-colors duration-200 truncate block">
                    {service.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Details */}
          <div className="col-span-2 sm:col-span-1">
            <h4 className="text-white font-semibold text-sm sm:text-base mb-3 sm:mb-4 relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-8 after:h-0.5 after:bg-orange-500 pb-1.5">Get Free Quote</h4>
            <div className="space-y-2.5 sm:space-y-3.5 text-xs sm:text-sm">
              <p className="flex items-center space-x-2.5 sm:space-x-3">
                <FaPhoneAlt className="text-orange-500 text-xs sm:text-sm" />
                <a href={`tel:${settings.phone1}`} onClick={() => gtag_report_conversion()} className="hover:text-white transition">{settings.phone1}</a>
              </p>
              <p className="flex items-center space-x-2.5 sm:space-x-3">
                <FaPhoneAlt className="text-orange-500 text-xs sm:text-sm" />
                <a href={`tel:${settings.phone2}`} onClick={() => gtag_report_conversion()} className="hover:text-white transition">{settings.phone2}</a>
              </p>
              <p className="flex items-center space-x-2.5 sm:space-x-3">
                <FaEnvelope className="text-orange-500 text-xs sm:text-sm" />
                <a href={`mailto:${settings.email}`} className="hover:text-white transition break-all">{settings.email}</a>
              </p>
              <p className="flex items-center space-x-2.5 sm:space-x-3">
                <FaClock className="text-orange-500 text-xs sm:text-sm" />
                <span>{settings.workingHours}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-t border-slate-900 pt-8 flex flex-col md:flex-row justify-between items-center text-xs space-y-4 md:space-y-0">
          <p>© {new Date().getFullYear()} Vengamamba Enterprises. All Rights Reserved.</p>
          <p className="text-slate-500">Designed and Developed by <a href="https://www.vikrin.com/" target="_blank" rel="noopener noreferrer" className="hover:text-orange-500 transition-colors">Vikrin Pvt Ltd</a></p>
          <div className="flex space-x-6 text-slate-500">
            <span className="hover:text-slate-400 cursor-pointer">Privacy Policy</span>
            <span className="hover:text-slate-400 cursor-pointer">Terms & Conditions</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
